import {
    Args, Certs,
    Methods,
    NcaLayerRequest,
    NcaLayerResponse
} from '../../types/ncaLayer';
import {Deferred} from '../../utils/deferred.util';
import {WebSocketService} from './websocket.service';

export type NcaAction = 'SIGN' | 'LOGIN';

const LOGIN_OID = "1.3.6.1.5.5.7.3.2"
const SIGN_OID = "1.3.6.1.5.5.7.3.4"

export class SignService {
    public service: WebSocketService;
    readonly module: string = 'kz.gov.pki.knca.basics'

    readonly NCA_LAYER_URL = 'wss://127.0.0.1:13579/';

    constructor() {
        this.service = new WebSocketService();
    }

    public async initSocket(resolve: () => void, reject: () => void) {
        if (this.service.ws && this.service.ws.readyState === WebSocket.OPEN) {
            resolve()
        } else {
            this.service.createObservableSocket(this.NCA_LAYER_URL, resolve, reject);
        }
    }

    public setLocale(language: string): Deferred<NcaLayerResponse> {
        return this.sendMessage(new NcaLayerRequest(null, Methods.setLocale, [language]));
    }

    public isReady(): boolean {
        return this.service.ws?.readyState === this.service.socketIsOpen;
    }

    public initPromise(): Promise<any> {
        return new Promise((resolve, reject) => {
            return this.initSocket(
                resolve as () => void,
                reject as () => void
            );
        });
    }

    signXmlNew(args: Args): Deferred<NcaLayerResponse> {
        return this.sendMessage(new NcaLayerRequest(null, Methods.sign, args, this.module))
    }

    signCms(args: Args): Deferred<NcaLayerResponse> {
        return this.sendMessage(new NcaLayerRequest(null, Methods.sign, args, this.module))
    }


    private sendMessage(model: NcaLayerRequest): Deferred<NcaLayerResponse> {
        return this.service.sendMessage(JSON.stringify(model, (key, value) => {
            if (value) {
                return value;
            }
        }));
    }

    prepareArgs(oids: string, chain: string[], data: string, format: 'xml' | 'cms' = 'xml', local: string = 'ru'): Args {
        return {
            allowedStorages: ['PKCS12', 'AKKaztokenStore', 'AKKZIDCardStore'],
            format: format,
            data: data,
            signingParams: {},
            signerParams: {
                extKeyUsageOids: [oids],
                chain: chain
            },
            locale: local,
        };
    }

    // This function was extracted for more readability and testability
    initializeCerts(type: NcaAction): Certs {
        let oids: string = type === 'LOGIN' ? LOGIN_OID : SIGN_OID
        let chain: string[] = []
            .filter((value: string) => !!value)
        return {oids, chain}
    }

    async callNcaLayer(type: NcaAction, xml: string, locale: string = 'ru') {
        await this.initPromise()

        const {oids, chain} = this.initializeCerts(type)
        const args = this.prepareArgs(oids, chain, xml, 'xml', locale)

        return this.signXmlNew(args).promise
    }

    async callNcaLayerCms(type: NcaAction, data: string, locale: string = 'ru') {
        await this.initPromise()

        const {oids, chain} = this.initializeCerts(type)
        const args = this.prepareArgs(oids, chain, data, 'cms', locale)

        return this.signCms(args).promise
    }
}
